import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { UsageService } from '../shared/services/usage.service';
import { UsageScope, ledgerLabel } from '../shared/models/usage.model';
import { resetsAtLocal, resetsIn, resetsOn } from '../shared/utils/usage-reset';

/**
 * Compact "my usage" widget for the AGENTHOME sidebar user-menu. Pure presentation
 * bound to UsageService signals — the service owns fetching/refresh.
 *
 * Quotas are enforced PER-LEDGER (each cost-center is independently capped), so the
 * per-ledger section is the PRIMARY, capped display: each ledger shows its own
 * used / limit + a level-coloured bar (brand red ok → amber ≥80% → danger ≥100%).
 * The combined "Total (all ledgers)" line at the top is INFORMATIONAL ONLY — a sum
 * of usage across ledgers, NOT an enforced cap — so it shows just the used number
 * (no "/limit") to avoid implying the combined total is the thing you're gated on.
 */
@Component({
  selector: 'app-usage-meter',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!unlimited()) {
    <div class="usage-meter" role="group" aria-label="Token usage">
      <div class="usage-meter__head">Usage</div>
      @if (loading() && !usage()) {
        <span class="usage-meter__muted">Loading usage…</span>
      } @else if (errored() && !usage()) {
        <span class="usage-meter__muted">Usage unavailable</span>
      } @else if (usage(); as u) {
        <!-- ── PRIMARY: per-ledger caps (each ledger is enforced separately) ── -->
        @if (u.ledgers && u.ledgers.length > 0) {
          <div class="usage-meter__head usage-meter__head--sub">Per ledger · this month</div>
          @for (lg of u.ledgers; track lg.cost_center) {
            <div class="usage-meter__row usage-meter__row--ledger">
              <span class="usage-meter__label">{{ ledgerLabel(lg.cost_center) }}</span>
              @if (lg.unlimited) {
                <span class="usage-meter__val">{{ fmt(lg.monthly.used_tokens) }} · Unlimited</span>
              } @else {
                <span class="usage-meter__val">{{ fmt(lg.monthly.used_tokens) }} / {{ fmt(lg.monthly.limit_tokens ?? 0) }}</span>
              }
            </div>
            @if (!lg.unlimited) {
              <div
                class="usage-meter__bar"
                role="progressbar"
                [attr.aria-valuenow]="pct(lg.monthly)"
                aria-valuemin="0"
                aria-valuemax="100"
                [attr.aria-valuetext]="pct(lg.monthly) + '% of this month\\'s ' + ledgerLabel(lg.cost_center) + ' tokens used'"
              >
                <div
                  class="usage-meter__fill"
                  [class]="'usage-meter__fill--' + level(lg.monthly, lg.unlimited)"
                  [style.width.%]="pct(lg.monthly)"
                ></div>
              </div>
            }
          }
        }

        <!-- ── INFORMATIONAL: combined total across all ledgers (NOT a cap) ── -->
        <div class="usage-meter__head usage-meter__head--sub" [title]="totalNote">
          Total (all ledgers)
        </div>
        <div class="usage-meter__row">
          <span class="usage-meter__label">Today</span>
          <span class="usage-meter__val">{{ fmt(u.daily.used_tokens) }} used</span>
        </div>
        @if (u.daily.resets_at) {
          <div class="usage-meter__reset" [title]="resetsAtLocal(u.daily.resets_at)">
            Resets {{ resetsIn(u.daily.resets_at) }}
          </div>
        }
        <div class="usage-meter__row usage-meter__row--sub">
          <span class="usage-meter__label">This month</span>
          <span class="usage-meter__val">{{ fmt(u.monthly.used_tokens) }} used</span>
        </div>
        @if (u.monthly.resets_at) {
          <div class="usage-meter__reset" [title]="resetsAtLocal(u.monthly.resets_at)">
            Resets {{ resetsOn(u.monthly.resets_at) }}
          </div>
        }
        <div class="usage-meter__note">{{ totalNote }}</div>
      }
    </div>
    }
  `,
  styles: [`
    /* Horizontal inset is owned by the host (.ah-user-menu app-usage-meter) so it
     * lines up with the other profile-menu rows; keep only vertical breathing room here. */
    .usage-meter { display: flex; flex-direction: column; gap: 4px; padding: 2px 0 4px; font-size: 12px; }
    .usage-meter__head {
      font-size: 0.62rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
      color: var(--wg-text-muted, #94a3b8); margin-bottom: 2px;
    }
    .usage-meter__row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
    .usage-meter__row--sub { margin-top: 7px; }
    .usage-meter__head--sub { margin-top: 11px; }
    .usage-meter__row--ledger { margin-top: 5px; }
    .usage-meter__label { color: var(--wg-text-muted, #6b7280); }
    .usage-meter__val { color: var(--wg-text, #111827); font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .usage-meter__muted { color: var(--wg-text-muted, #6b7280); }
    /* Muted "Resets …" hint under each total window (daily countdown / monthly date). */
    .usage-meter__reset { margin-top: 1px; font-size: 0.68rem; line-height: 1.3; color: var(--wg-text-muted, #94a3b8); }
    /* Clarifies that the combined total above it is informational, not a cap. */
    .usage-meter__note { margin-top: 5px; font-size: 0.68rem; line-height: 1.35; color: var(--wg-text-muted, #94a3b8); }
    .usage-meter__bar { height: 5px; border-radius: 999px; background: var(--wg-surface-3, #ededf0); overflow: hidden; }
    /* Prototype palette: brand red (ok) -> amber (approaching) -> red (reached). */
    .usage-meter__fill { height: 100%; border-radius: 999px; transition: width 0.3s ease, background 0.2s ease; background: #d81f2c; }
    .usage-meter__fill--approaching { background: #e0a03a; }
    .usage-meter__fill--reached { background: #e5484d; }
  `],
})
export class UsageMeterComponent {
  private readonly svc = inject(UsageService);
  readonly usage = this.svc.usage;
  readonly loading = this.svc.loading;
  readonly errored = this.svc.errored;
  readonly unlimited = this.svc.unlimited;

  /** Explains the combined "Total (all ledgers)" line — it sums usage across
   *  ledgers and is NOT the enforced limit; each ledger above is capped on its own. */
  readonly totalNote = 'Each ledger is capped separately above. This total is informational only.';

  private readonly _fmt = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
  fmt = (n: number): string => this._fmt.format(n ?? 0);

  ledgerLabel = ledgerLabel;

  // "Resets …" hint formatters — framework-free (see usage-reset.ts). Daily is a
  // countdown ("in 6h"), monthly a local date ("Aug 1"); the exact local datetime
  // is the hover tooltip. Unit-tested in the studio-ui copy of usage-reset.
  resetsIn = resetsIn;
  resetsOn = resetsOn;
  resetsAtLocal = resetsAtLocal;

  /** Clamped 0–100 fill for a ledger scope's bar. */
  pct(scope: UsageScope): number {
    return Math.max(0, Math.min(100, Math.round(scope?.pct_used ?? 0)));
  }

  /** Bar colour level for a ledger scope — mirrors UsageService.levelFor
   *  (≥100 reached, ≥80 approaching, else ok). */
  level(scope: UsageScope, unlimited: boolean): 'ok' | 'approaching' | 'reached' {
    const p = scope?.pct_used;
    if (unlimited || p == null) return 'ok';
    if (p >= 100) return 'reached';
    if (p >= 80) return 'approaching';
    return 'ok';
  }
}
